import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Check, Search, X } from 'lucide-react';
import {
  filterBooksByLanguage,
  getLanguageCounts,
  languageLabel,
  sortedLanguageIds,
} from '../utils/bookFilters';
import { getBookStoryPath } from '../utils/storyPath';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ALWAYS_VISIBLE_STORY_PATHS = ['animals', 'nature', 'adventure'];

const FILTER_PILL_BASE =
  'px-4 py-2 rounded-full text-sm font-medium transition-all';
const FILTER_PILL_INACTIVE =
  'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm';
const FILTER_PILL_SELECTED_STYLE = {
  background: 'linear-gradient(135deg, var(--th-md) 0%, var(--th-lt) 50%, var(--th-lt) 100%)',
  color: '#ffffff',
};

export const PATH_META = {
  animals: { label: 'Animals', type: 'specific' },
  nature: { label: 'Nature', type: 'specific' },
  adventure: { label: 'Adventure', type: 'specific' },
  'business-basics': { label: 'Business Basics', type: 'general' },
  'financial-literacy': { label: 'Financial Literacy', type: 'general' },
  diversity: { label: 'Diversity', type: 'general' },
  'indian-stories': { label: 'Indian Stories', type: 'general' },
  'stem-sparks': { label: 'STEM Sparks', type: 'general' },
  'big-feelings': { label: 'Big Feelings', type: 'general' },
  'brave-little-ones': { label: 'Brave Little Ones', type: 'specific' },
  'friends-forever': { label: 'Friends Forever', type: 'specific' },
  'wild-wonders': { label: 'Wild Wonders', type: 'specific' },
  'family-first': { label: 'Family First', type: 'specific' },
  'once-upon-a-dream': { label: 'Once Upon a Dream', type: 'specific' },
  'the-real-world': { label: 'The Real World', type: 'specific' }
};

const GardenPage = ({ onOpenReader }) => {
  const { token } = useAuth();
  const [books, setBooks] = useState([]);
  const [filter, setFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const res = await fetch(`${API}/books`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        console.log('📚 Fetched books with progress data:', data.slice(0, 2));
        setBooks(data);
      }
    } catch (err) {
      console.error('Failed to fetch books:', err);
    } finally {
      setLoading(false);
    }
  };

  const booksForPathScope = filterBooksByLanguage(books, languageFilter);

  const pathCounts = booksForPathScope.reduce((acc, book) => {
    const key = getBookStoryPath(book);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const booksForLanguageScope =
    filter === 'all'
      ? books
      : books.filter((b) => getBookStoryPath(b) === filter);

  const languageCounts = getLanguageCounts(booksForLanguageScope);
  const languageIds = sortedLanguageIds(languageCounts);

  const allPathIds = Object.keys(pathCounts);
  const orderedPathIds = Object.keys(PATH_META).filter(
    (id) => allPathIds.includes(id) || ALWAYS_VISIBLE_STORY_PATHS.includes(id)
  );
  const unknownPathIds = allPathIds.filter((id) => !PATH_META[id]);
  const combinedPathIds = [...orderedPathIds, ...unknownPathIds];

  const specificPaths = combinedPathIds.filter((id) => (PATH_META[id]?.type || 'specific') === 'specific');
  const generalPaths = combinedPathIds.filter((id) => (PATH_META[id]?.type || 'specific') === 'general');

  const filters = [
    { id: 'all', label: 'All Books' },
    ...combinedPathIds.map((pathId) => ({
      id: pathId,
      label: PATH_META[pathId]?.label || pathId.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    }))
  ];

  const languageFilteredBooks = filterBooksByLanguage(books, languageFilter);

  // Filter books by story path (after language)
  const pathFilteredBooks =
    filter === 'all'
      ? languageFilteredBooks
      : languageFilteredBooks.filter((b) => getBookStoryPath(b) === filter);

  // Apply search filter to both book title and story path name
  const filteredBooks = searchQuery.trim() === ''
    ? pathFilteredBooks
    : pathFilteredBooks.filter((book) => {
        const searchLower = searchQuery.toLowerCase();
        
        // Search by book title
        const titleMatch = book.title.toLowerCase().includes(searchLower);
        
        // Search by story path name
        const storyPath = getBookStoryPath(book);
        const pathLabel = PATH_META[storyPath]?.label || storyPath.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        const pathMatch = pathLabel.toLowerCase().includes(searchLower);
        
        return titleMatch || pathMatch;
      });

  const getBookStatus = (book) => {
    const hasProgress = !!book.progress;
    const isCompleted = book.progress?.completed;
    const currentPage = book.progress?.current_page;
    
    // Debug logging
    if (book.id === 'brave-lion') {
      console.log(`🦁 Brave Lion Status:`, { 
        hasProgress, 
        isCompleted, 
        currentPage,
        fullProgress: book.progress 
      });
    }
    
    if (book.progress?.completed) return 'read';
    if (book.progress?.current_page > 0) return 'reading';
    return 'new';
  };

  // Show selected path (or default top path) in the journey strip.
  const activePath = filter === 'all'
    ? (filters[1]?.id || 'all')
    : filter;
  const journeySource =
    activePath === 'all'
      ? languageFilteredBooks
      : languageFilteredBooks.filter((b) => getBookStoryPath(b) === activePath);
  const storyPathBooks = journeySource.slice(0, 6);
  const storyPathLabel = activePath === 'all'
    ? 'All Books'
    : (PATH_META[activePath]?.label || activePath.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-4xl animate-bounce">📚</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up" data-testid="garden-page">
      {/* Page Hero - Theme Gradient Banner */}
      <div 
        className="rounded-3xl p-8"
        style={{ 
          background: 'linear-gradient(135deg, var(--th-md) 0%, var(--th-lt) 50%, var(--th-lt) 100%)',
        }}
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Fredoka' }}>
            Book Garden
          </h1>
        </div>
        <p className="text-white/90 text-sm ml-1">Pick a book and dive right in! 📚</p>
      </div>

      {/* Story Path Section */}
      <div 
        className="rounded-2xl p-5"
        style={{ background: '#F8FAF9', border: '1px solid #E8EDE9' }}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <span className="text-red-500">📍</span>
            <span className="font-semibold text-gray-700 text-sm tracking-wide">YOUR STORY PATH</span>
          </div>
          <span className="text-sm text-gray-500">Story Path · {storyPathLabel}</span>
        </div>
        
        <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
          {storyPathBooks.map((book, idx) => {
            const status = getBookStatus(book);
            const isCompleted = status === 'read';
            const isCurrent = status === 'reading';
            
            return (
              <React.Fragment key={book.id}>
                {idx > 0 && (
                  <div
                    className="w-8 h-0.5 flex-shrink-0"
                    style={{ background: isCompleted ? 'var(--th-md)' : '#D1D5DB' }}
                  />
                )}
                <button
                  onClick={() => onOpenReader(book.id)}
                  className="flex flex-col items-center gap-2 flex-shrink-0 group"
                >
                  <div 
                    className={`w-20 h-20 rounded-full flex items-center justify-center overflow-hidden transition-all shadow-md ${
                      isCurrent ? 'ring-4 ring-offset-2' : ''
                    }`}
                    style={{
                      background: isCompleted ? 'var(--th-md)' : isCurrent ? 'var(--th-lt)' : 'var(--th-surf)',
                      border: isCurrent ? '3px solid var(--th-md)' : isCompleted ? '3px solid var(--th-md)' : '3px solid color-mix(in srgb, var(--th-lt) 40%, #e5e7eb)',
                      boxShadow: isCurrent ? '0 0 0 4px color-mix(in srgb, var(--th-lt) 35%, white)' : undefined,
                      opacity: !isCompleted && !isCurrent ? 0.6 : 1,
                    }}
                  >
                    {isCompleted ? (
                      <Check className="w-8 h-8 text-white" strokeWidth={3} />
                    ) : book.cover_image ? (
                      <img
                        src={`${BACKEND_URL}${book.cover_image}`}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const wrap = e.target.parentElement;
                          if (wrap) wrap.textContent = book.emoji || '📚';
                        }}
                      />
                    ) : (
                      <span className="text-3xl">{book.emoji || '📚'}</span>
                    )}
                  </div>
                  <span className="text-xs font-medium text-gray-700 text-center max-w-24 leading-tight">
                    {book.title.length > 15 ? book.title.split(' ').slice(0, 2).join(' ') : book.title}
                  </span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Language filter */}
      {languageIds.length > 0 && (
        <div className="space-y-3" data-testid="garden-language-filters">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white"
              style={{
                background: 'linear-gradient(135deg, var(--th-md), var(--th-lt))',
              }}
            >
              🌐
            </span>
            Language
          </h2>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setLanguageFilter('all')}
              data-testid="language-filter-all"
              className={`${FILTER_PILL_BASE} ${
                languageFilter === 'all' ? 'shadow-md scale-105' : FILTER_PILL_INACTIVE
              }`}
              style={languageFilter === 'all' ? FILTER_PILL_SELECTED_STYLE : undefined}
            >
              All Languages ({booksForLanguageScope.length})
            </button>
            {languageIds.map((langId) => {
              const selected = languageFilter === langId;
              return (
                <button
                  key={langId}
                  type="button"
                  onClick={() => setLanguageFilter(langId)}
                  data-testid={`language-filter-${langId}`}
                  className={`${FILTER_PILL_BASE} ${
                    selected ? 'shadow-md scale-105' : FILTER_PILL_INACTIVE
                  }`}
                  style={selected ? FILTER_PILL_SELECTED_STYLE : undefined}
                >
                  {languageLabel(langId)} ({languageCounts[langId]})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Story Paths - Small Capsules */}
      <div className="space-y-3">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <span
            className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white"
            style={{
              background: 'linear-gradient(135deg, var(--th-md), var(--th-lt))',
            }}
          >
            📚
          </span>
          Story Path
        </h2>
        
        <div className="flex flex-wrap gap-2">
          {/* All Books Button */}
          <button
            onClick={() => setFilter('all')}
            className={`${FILTER_PILL_BASE} ${
              filter === 'all' ? 'shadow-md scale-105' : FILTER_PILL_INACTIVE
            }`}
            style={filter === 'all' ? FILTER_PILL_SELECTED_STYLE : undefined}
          >
            All Books ({booksForPathScope.length})
          </button>
          
          {/* Individual Path Capsules */}
          {combinedPathIds.map((pathId) => {
            const pathLabel = PATH_META[pathId]?.label || pathId.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            const bookCount = pathCounts[pathId] || 0;
            const selected = filter === pathId;
            
            return (
              <button
                key={pathId}
                onClick={() => setFilter(pathId)}
                className={`${FILTER_PILL_BASE} ${
                  selected ? 'shadow-md scale-105' : FILTER_PILL_INACTIVE
                }`}
                style={selected ? FILTER_PILL_SELECTED_STYLE : undefined}
              >
                {pathLabel} ({bookCount})
              </button>
            );
          })}
        </div>
      </div>

      {/* All Books Grid */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-gray-800">All Books</h2>
        
        {/* Search Bar */}
        <div className="relative">
          <div className="flex items-center bg-white rounded-2xl border-2 border-gray-200 hover:border-[var(--th-md)] focus-within:border-[var(--th-md)] transition-colors px-4 py-3">
            <Search className="w-5 h-5 text-gray-400 mr-3" />
            <input
              type="text"
              placeholder="Search by book name, story path..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none text-gray-700 placeholder-gray-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="ml-2 p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          
          {/* Search results count */}
          {searchQuery.trim() !== '' && (
            <p className="text-xs text-gray-500 mt-2">
              Found {filteredBooks.length} book{filteredBooks.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>

      {/* Book Grid */}
      {filteredBooks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="text-5xl mb-4">🔍</div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No books found</h3>
          <p className="text-sm text-gray-500 mb-4">
            {searchQuery.trim() !== ''
              ? 'Try searching with different keywords'
              : languageFilter !== 'all'
                ? `No books found for ${languageLabel(languageFilter)}`
                : 'No books available for this story path'}
          </p>
          {searchQuery.trim() !== '' && (
            <button
              onClick={() => setSearchQuery('')}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
            >
              Clear Search
            </button>
          )}
          {searchQuery.trim() === '' && languageFilter !== 'all' && (
            <button
              type="button"
              onClick={() => setLanguageFilter('all')}
              className="px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium hover:opacity-90"
              style={{ background: 'var(--th-md)' }}
            >
              Show All Languages
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {filteredBooks.map(book => {
            const status = getBookStatus(book);
            const isRead = status === 'read';
            const isReading = status === 'reading';
            
            return (
              <button
                key={book.id}
                onClick={() => onOpenReader(book.id)}
                data-testid={`book-${book.id}`}
                className="rounded-2xl p-4 text-center transition-all hover:shadow-xl hover:scale-105 group"
                style={{
                  background: '#FFFFFF',
                  border: (isRead || isReading) ? '3px solid var(--th-md)' : '2px solid #E8EDE9'
                }}
              >
                {/* Book Cover/Emoji */}
                <div className="mb-3 flex justify-center">
                  {book.cover_image ? (
                    <div className="w-16 h-20 rounded-lg overflow-hidden bg-gray-100">
                      <img 
                        src={`${BACKEND_URL}${book.cover_image}`}
                        alt={book.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.parentElement.innerHTML = `<span class="text-4xl flex items-center justify-center h-full">${book.emoji}</span>`;
                        }}
                      />
                    </div>
                  ) : (
                    <span className="text-5xl">{book.emoji}</span>
                  )}
                </div>
                
                {/* Book Title */}
                <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">
                  {book.title}
                </h3>
                
                {/* Level & Pages */}
                <p className="text-xs text-gray-500 mb-3">
                  Level {book.level} · {book.pages} pages
                </p>
                
                {/* Status Badge */}
                <div 
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                    isRead
                      ? 'text-white'
                      : isReading
                        ? 'text-white'
                        : 'bg-transparent text-gray-500'
                  }`}
                  style={{
                    background: (isRead || isReading) ? 'var(--th-md)' : undefined
                  }}
                >
                  {isRead && <><Check className="w-3 h-3" /> Read</>}
                  {isReading && <><BookOpen className="w-3 h-3" /> Up Next</>}
                  {status === 'new' && 'Just for fun'}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GardenPage;
